import { Controller } from "@hotwired/stimulus";
import { Tooltip } from "bootstrap";
import Logger from '../lib/logger';
/**
 * Tooltip Controller
 * Generic controller to manage Bootstrap Tooltips.
 *
 * Usage:
 * <span data-controller="tooltip"
 *       data-bs-toggle="tooltip"
 *       title="My Tooltip"
 *       data-tooltip-placement-value="top">...</span>
 */
class default_1 extends Controller {
    constructor() {
        super(...arguments);
        this.tooltip = null;
        this.hideTooltip = () => {
            if (this.tooltip) {
                this.tooltip.hide();
            }
        };
    }
    connect() {
        // Lazy initialization on first interaction or if visible
        if (this.isVisible()) {
            requestAnimationFrame(() => this.initializeTooltip());
        }
        else {
            this.element.addEventListener('mouseenter', () => this.initializeTooltip(), { once: true });
        }
        // Hide tooltip on click to prevent transition errors during Turbo navigation
        this.element.addEventListener('click', this.hideTooltip);
    }
    isVisible() {
        return !!(this.element.offsetWidth || this.element.offsetHeight || this.element.getClientRects().length);
    }
    disconnect() {
        this.element.removeEventListener('click', this.hideTooltip);
        if (this.tooltip) {
            try {
                this.tooltip.hide(); // Hide before dispose to cancel any pending transitions
                this.tooltip.dispose();
            }
            catch (e) {
                Logger.debug('Error disposing tooltip', e);
            }
            this.tooltip = null;
        }
    }
    initializeTooltip() {
        try {
            const options = {
                container: this.hasContainerValue ? this.containerValue : 'body',
                trigger: this.hasTriggerValue ? this.triggerValue : 'hover focus',
                delay: { show: 150, hide: 100 },
                fallbackPlacements: ['top', 'bottom', 'left', 'right']
            };
            // Strictly ensure placement is a valid string before assigning
            if (this.hasPlacementValue && typeof this.placementValue === 'string' && this.placementValue.trim().length > 0) {
                options.placement = this.placementValue;
            }
            else {
                // Let Bootstrap fallback to its default or data-bs-placement
                options.placement = 'top';
            }
            // Ensure boundary is a valid Bootstrap 5/Popper 2 value
            if (this.hasBoundaryValue) {
                options.boundary = this.boundaryValue;
            }
            else {
                options.boundary = 'clippingParents'; // Popper v2 default (viewport is invalid for boundary in v2)
            }
            if (this.hasTitleValue)
                options.title = this.titleValue;
            if (this.hasCustomClassValue)
                options.customClass = this.customClassValue;
            if (this.tooltip)
                return; // Prevent double initialization
            this.tooltip = new Tooltip(this.element, options);
            Logger.debug('Tooltip initialized', { element: this.element, options });
        }
        catch (error) {
            Logger.error('Failed to initialize tooltip', error, { element: this.element });
        }
    }
}
default_1.values = {
    placement: String,
    container: String,
    trigger: String,
    boundary: String,
    title: String,
    customClass: String
};
export default default_1;
